import { useContext, useEffect, useMemo, useState } from 'react';

import { Box, FormControl, Input, Spinner, Text } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import NumberFormat, { NumberFormatValues } from 'react-number-format';

import { Deal } from '../../gql/dealGql';
import { TermEnum } from '../../gql/financialInfoGql';
import {
  Deal as GeneratedDeal,
  TtGetFeesSourceType,
  usePaymentEstimateQuery,
} from '../../gql/generated/graphql';

import SharedSelect from '../shared/Select';

import { PaymentEstimateValues } from './PaymentEstimateModal';

import useDebounce from '../../hooks/useDebounce';
import { DealContext } from '../../libs/DealContext';
import { formatMoney } from '../../libs/utils';
import { mapDealToGetPaymentEstimateVariables } from '../../utils/deals';

type PaymentEstimateFormProps = {
  deal: Deal;
};

const PaymentEstimateForm = ({ deal }: PaymentEstimateFormProps) => {
  const { setUserChangedSomething } = useContext(DealContext);
  const { values, setFieldValue } = useFormikContext<PaymentEstimateValues>();

  const [debouncedMoneyDown, setDebouncedMoneyDown] = useState(values.money_down);

  const debouncedSetMoneyDown = useDebounce((value: number) => {
    setDebouncedMoneyDown(value);
  }, 500);

  useEffect(() => {
    debouncedSetMoneyDown(values.money_down);
  }, [values.money_down]);

  const hasZip = !!deal?.customer?.address?.zip;

  const { data, loading, error } = usePaymentEstimateQuery({
    skip: !hasZip,
    fetchPolicy: 'network-only',
    variables: {
      ...mapDealToGetPaymentEstimateVariables(
        {
          ...deal,
          financial_info: {
            ...deal.financial_info,
            // When term or money down is changed, we need to get a new estimate
            term: values.term,
            money_down: debouncedMoneyDown,
          },
        } as GeneratedDeal,
        TtGetFeesSourceType.DdPaymentEstimate,
      ),
    },
  });

  const paymentEstimateRange = useMemo(() => {
    if (!data?.paymentEstimate) {
      return '$XXX - $XXX';
    }

    const { paymentEstimateLow, paymentEstimateHigh } = data.paymentEstimate;
    return `${formatMoney(paymentEstimateLow, {
      noDecimal: true,
    })} - ${formatMoney(paymentEstimateHigh, { noDecimal: true })}`;
  }, [data?.paymentEstimate]);

  return (
    <>
      <FormControl>
        <SharedSelect
          label="Term"
          name="term"
          emptyOption={false}
          value={values.term}
          options={Object.values(TermEnum).map((term) => ({
            label: String(term),
            value: String(term),
          }))}
          onChange={(e) => {
            setUserChangedSomething(true);
            setFieldValue('term', Number(e.target.value));
          }}
        />
        <Box>
          <Text fontSize="sm" mt={3} mb={2}>
            Money Down
          </Text>
          <Input
            as={NumberFormat}
            name="money_down"
            thousandSeparator
            prefix="$"
            value={values.money_down}
            onValueChange={(vals: NumberFormatValues) => {
              setUserChangedSomething(true);
              setFieldValue('money_down', vals.floatValue ?? 0);
            }}
            maxLength={13}
          />
        </Box>
      </FormControl>
      <Box>
        <Text fontSize="sm" mt={3} mb={2}>
          Estimate
        </Text>
        <Box
          borderRadius="8px"
          bgColor={error ? 'red.100' : 'azureishWhite'}
          fontSize={{ base: 'lg', md: '2xl' }}
          fontWeight="normal"
          textAlign="center"
          height="75px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {loading ? (
            <Spinner variant="thick" />
          ) : error ? (
            <Text fontSize="18px" color="errorsRed">
              {hasZip && !error.message.includes('Invalid Zip Code')
                ? error.message
                : 'Invalid Zip Code'}
            </Text>
          ) : (
            paymentEstimateRange
          )}
        </Box>
      </Box>
    </>
  );
};

export default PaymentEstimateForm;
